<template>
  <div class="page-container">
    <div class="settings-form">
      <!-- 顶部引导区域 -->
      <div class="guide-section">
        <div class="guide-content">
          <h2 class="guide-title">
            <el-icon class="guide-icon">
              <Platform />
            </el-icon>
            平台管理
          </h2>
          <p class="guide-desc">统一管理小程序、公众号和H5平台的配置信息</p>
        </div>
        <div class="guide-actions">
          <el-button type="primary" @click="fetchAllSettings" :loading="loading.refresh">
            <el-icon>
              <Refresh />
            </el-icon>
            刷新数据
          </el-button>
        </div>
      </div>

      <!-- 小程序管理 -->
      <el-card class="platform-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-title-section">
              <el-icon class="card-icon mini-icon">
                <Iphone />
              </el-icon>
              <span class="card-title">小程序管理</span>
              <el-tag v-if="miniprogramForm.appid" type="success" size="small">已配置</el-tag>
              <el-tag v-else type="info" size="small">未配置</el-tag>
            </div>
            <el-button type="primary" size="small" @click="saveMiniprogram" :loading="loading.miniprogram"
              class="save-btn">
              <el-icon>
                <Check />
              </el-icon>
              保存设置
            </el-button>
          </div>
        </template>

        <div class="form-content">
          <el-form :model="miniprogramForm" label-width="120px">
            <el-form-item label="小程序AppID">
              <el-input v-model="miniprogramForm.appid" placeholder="请输入小程序AppID" clearable />
            </el-form-item>

            <el-form-item label="小程序密钥">
              <el-input v-model="miniprogramForm.secret" placeholder="请输入小程序密钥" show-password clearable />
            </el-form-item>

            <el-form-item label="上传密钥">
              <el-input v-model="miniprogramForm.private_key" placeholder="请输入小程序上传密钥" type="textarea" :rows="4"
                clearable class="private-key-input" />
              <div class="key-tip">
                <el-icon>
                  <InfoFilled />
                </el-icon>
                上传密钥用于代码上传，请妥善保管
              </div>
            </el-form-item>
            <!-- 在小程序管理的 el-form 中添加这个表单项 -->
            <el-form-item label="访问地址">
              <el-input v-model="accessUrls.miniprogram" readonly class="url-input">
                <template #append>
                  <el-button @click="copyToClipboard(accessUrls.miniprogram)" class="copy-btn">
                    <el-icon>
                      <DocumentCopy />
                    </el-icon>
                  </el-button>
                </template>
              </el-input>
              <div class="url-tip">此路径为小程序访问路径，需在小程序代码中配置</div>
            </el-form-item>
            <el-form-item label="手机号授权">
              <div class="switch-container">
                <el-switch v-model="miniprogramForm.enable_phone_auth" :active-value="1" :inactive-value="0"
                  active-color="#13ce66" />
                <span class="switch-label" :class="{ active: miniprogramForm.enable_phone_auth }">
                  {{ miniprogramForm.enable_phone_auth ? '已开启' : '已关闭' }}
                </span>
              </div>
            </el-form-item>

            <el-form-item label="操作">
              <div class="action-buttons">
                <el-button type="success" @click="uploadMiniprogram"
                  :disabled="!miniprogramForm.appid || !miniprogramForm.private_key" :loading="loading.upload"
                  class="action-btn">
                  <el-icon>
                    <Upload />
                  </el-icon>
                  {{ loading.upload ? '上传中...' : '上传小程序' }}
                </el-button>
                <!-- <el-button type="warning" @click="generateMiniprogramQrcode" :disabled="!miniprogramForm.appid"
                  class="action-btn">
                  <el-icon>
                    <Picture />
                  </el-icon>
                  生成二维码
                </el-button> -->
              </div>
            </el-form-item>

            <el-form-item v-if="miniprogramForm.last_update_time" label="最后更新">
              <div class="update-info">
                <el-icon>
                  <Clock />
                </el-icon>
                <span class="update-time">{{ miniprogramForm.last_update_time }}</span>
              </div>
            </el-form-item>
          </el-form>
        </div>
      </el-card>

      <!-- 公众号管理 -->
      <el-card class="platform-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-title-section">
              <el-icon class="card-icon offi-icon">
                <ChatDotRound />
              </el-icon>
              <span class="card-title">公众号管理</span>
              <el-tag v-if="offiaccountForm.appid" type="success" size="small">已配置</el-tag>
              <el-tag v-else type="info" size="small">未配置</el-tag>
            </div>
            <el-button type="primary" size="small" @click="saveOffiaccount" :loading="loading.offiaccount"
              class="save-btn">
              <el-icon>
                <Check />
              </el-icon>
              保存设置
            </el-button>
          </div>
        </template>

        <div class="form-content">
          <el-form :model="offiaccountForm" label-width="120px">
            <el-form-item label="公众号AppID">
              <el-input v-model="offiaccountForm.appid" placeholder="请输入公众号AppID" clearable />
            </el-form-item>

            <el-form-item label="公众号密钥">
              <el-input v-model="offiaccountForm.secret" placeholder="请输入公众号密钥" show-password clearable />
            </el-form-item>

            <el-form-item label="回调链接">
              <el-input v-model="offiaccountForm.callback_url" placeholder="请输入回调链接" clearable />
            </el-form-item>

            <el-form-item label="手机号授权">
              <div class="switch-container">
                <el-switch v-model="offiaccountForm.enable_phone_auth" :active-value="1" :inactive-value="0"
                  active-color="#13ce66" />
                <span class="switch-label" :class="{ active: offiaccountForm.enable_phone_auth }">
                  {{ offiaccountForm.enable_phone_auth ? '已开启' : '已关闭' }}
                </span>
              </div>
            </el-form-item>

            <el-form-item label="访问地址">
              <el-input v-model="accessUrls.offiaccount" readonly class="url-input">
                <template #append>
                  <el-button @click="copyToClipboard(accessUrls.offiaccount)" class="copy-btn">
                    <el-icon>
                      <DocumentCopy />
                    </el-icon>
                  </el-button>
                </template>
              </el-input>
            </el-form-item>

            <el-form-item label="操作">
              <div class="action-buttons">
                <el-button type="warning" @click="generateOffiaccountQrcode" :disabled="!offiaccountForm.appid"
                  class="action-btn">
                  <el-icon>
                    <Picture />
                  </el-icon>
                  生成二维码
                </el-button>
              </div>
            </el-form-item>

            <el-form-item v-if="offiaccountQrcode" label="公众号二维码">
              <div class="qrcode-container">
                <img :src="offiaccountQrcode" alt="公众号二维码" class="qrcode-image" />
                <p class="qrcode-tip">扫描二维码访问公众号</p>
              </div>
            </el-form-item>

            <el-form-item v-if="offiaccountForm.last_update_time" label="最后更新">
              <div class="update-info">
                <el-icon>
                  <Clock />
                </el-icon>
                <span class="update-time">{{ offiaccountForm.last_update_time }}</span>
              </div>
            </el-form-item>
          </el-form>
        </div>
      </el-card>

      <!-- H5管理 -->
      <el-card class="platform-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-title-section">
              <el-icon class="card-icon h5-icon">
                <Monitor />
              </el-icon>
              <span class="card-title">H5管理</span>
              <el-tag type="info" size="small">基础配置</el-tag>
            </div>
            <el-button type="primary" size="small" @click="saveH5" :loading="loading.h5" class="save-btn">
              <el-icon>
                <Check />
              </el-icon>
              保存设置
            </el-button>
          </div>
        </template>

        <div class="form-content">
          <el-form :model="h5Form" label-width="120px">
            <el-form-item label="访问地址">
              <el-input v-model="accessUrls.h5" readonly class="url-input">
                <template #append>
                  <el-button @click="copyToClipboard(accessUrls.h5)" class="copy-btn">
                    <el-icon>
                      <DocumentCopy />
                    </el-icon>
                  </el-button>
                </template>
              </el-input>
              <div class="url-tip">此地址为您的H5页面访问链接</div>
            </el-form-item>

            <el-form-item label="手机号授权">
              <div class="switch-container">
                <el-switch v-model="h5Form.enable_phone_auth" :active-value="1" :inactive-value="0"
                  active-color="#13ce66" />
                <span class="switch-label" :class="{ active: h5Form.enable_phone_auth }">
                  {{ h5Form.enable_phone_auth ? '已开启' : '已关闭' }}
                </span>
              </div>
            </el-form-item>

            <el-form-item v-if="h5Form.last_update_time" label="最后更新">
              <div class="update-info">
                <el-icon>
                  <Clock />
                </el-icon>
                <span class="update-time">{{ h5Form.last_update_time }}</span>
              </div>
            </el-form-item>
          </el-form>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  Platform, Refresh, Iphone, Check, Upload, Picture, DocumentCopy,
  ChatDotRound, Monitor, Clock, InfoFilled
} from '@element-plus/icons-vue';

export default {
  name: 'PlatformManagement',
  components: {
    Platform, Refresh, Iphone, Check, Upload, Picture, DocumentCopy,
    ChatDotRound, Monitor, Clock, InfoFilled
  },
  data() {
    return {
      loading: {
        miniprogram: false,
        offiaccount: false,
        h5: false,
        qrcode: false,
        refresh: false,
        upload: false
      },
      miniprogramForm: {
        appid: '',
        secret: '',
        private_key: '',
        enable_phone_auth: 0,
        last_update_time: null
      },
      offiaccountForm: {
        appid: '',
        secret: '',
        callback_url: '',
        enable_phone_auth: 0,
        last_update_time: null
      },
      h5Form: {
        enable_phone_auth: 0,
        last_update_time: null
      },
      accessUrls: {
        miniprogram: '', // 新增小程序访问路径
        offiaccount: '',
        h5: ''
      },
      offiaccountQrcode: ''
    };
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}');
    }
  },
  mounted() {
    this.fetchAllSettings();
    // 设置小程序访问路径
    this.setMiniprogramAccessUrl();
  },
  methods: {

    // 设置小程序访问路径
    setMiniprogramAccessUrl() {
      const storeId = this.userInfo.id;
      this.accessUrls.miniprogram = `pages/home/home?store_id=${storeId}`;
    },
    // 获取所有设置
    // 在获取所有设置后也更新小程序路径
    async fetchAllSettings() {
      this.loading.refresh = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'get_all_settings',
            store_id: this.userInfo.id
          }
        );

        if (response.data.data) {
          const data = response.data.data;
          this.miniprogramForm = data.miniprogram;
          this.offiaccountForm = data.offiaccount;
          this.h5Form = data.h5;
          this.accessUrls = data.access_urls;

          // 确保小程序访问路径正确设置
          this.accessUrls.miniprogram = `pages/home/home?store_id=${this.userInfo.id}`;

          ElMessage.success('数据刷新成功');
        }
      } catch (error) {
        ElMessage.error('获取平台设置失败');
        console.error('获取平台设置错误:', error);
      } finally {
        this.loading.refresh = false;
      }
    },

    // 保存小程序设置
    async saveMiniprogram() {
      if (!this.miniprogramForm.appid || !this.miniprogramForm.secret) {
        ElMessage.warning('请填写小程序AppID和密钥');
        return;
      }

      this.loading.miniprogram = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'save_miniprogram',
            store_id: this.userInfo.id,
            ...this.miniprogramForm
          }
        );

        if (response.data.success) {
          ElMessage.success('小程序设置保存成功');
          this.fetchAllSettings();
        } else {
          ElMessage.error(response.data.error || '保存失败');
        }
      } catch (error) {
        ElMessage.error('保存小程序设置失败');
        console.error('保存小程序设置错误:', error);
      } finally {
        this.loading.miniprogram = false;
      }
    },

    // 保存公众号设置
    async saveOffiaccount() {
      if (!this.offiaccountForm.appid || !this.offiaccountForm.secret) {
        ElMessage.warning('请填写公众号AppID和密钥');
        return;
      }

      this.loading.offiaccount = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'save_offiaccount',
            store_id: this.userInfo.id,
            ...this.offiaccountForm
          }
        );

        if (response.data.success) {
          ElMessage.success('公众号设置保存成功');
          this.fetchAllSettings();
        } else {
          ElMessage.error(response.data.error || '保存失败');
        }
      } catch (error) {
        ElMessage.error('保存公众号设置失败');
        console.error('保存公众号设置错误:', error);
      } finally {
        this.loading.offiaccount = false;
      }
    },

    // 保存H5设置
    async saveH5() {
      this.loading.h5 = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'save_h5',
            store_id: this.userInfo.id,
            enable_phone_auth: this.h5Form.enable_phone_auth
          }
        );

        if (response.data.success) {
          ElMessage.success('H5设置保存成功');
          this.fetchAllSettings();
        } else {
          ElMessage.error(response.data.error || '保存失败');
        }
      } catch (error) {
        ElMessage.error('保存H5设置失败');
        console.error('保存H5设置错误:', error);
      } finally {
        this.loading.h5 = false;
      }
    },

    // 上传小程序
    async uploadMiniprogram() {
      if (!this.miniprogramForm.appid) {
        ElMessage.warning('请先配置小程序AppID');
        return;
      }

      if (!this.miniprogramForm.private_key) {
        ElMessage.warning('请先配置小程序上传密钥');
        return;
      }

      try {
        await ElMessageBox.confirm(
          '确定要上传小程序代码吗？上传过程可能需要几分钟，请勿关闭页面。',
          '确认上传',
          {
            confirmButtonText: '确定上传',
            cancelButtonText: '取消',
            type: 'warning',
          }
        );

        this.loading.upload = true;
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'upload_miniprogram',
            store_id: this.userInfo.id
          }
        );

        // 处理不同的响应格式
        if (response.data.success || response.data.status === 'success') {
          ElMessage.success('小程序上传成功！');
        } else if (response.data.error) {
          ElMessage.error(response.data.error);
        } else {
          ElMessage.error('上传失败，请检查配置信息');
        }
      } catch (error) {
        if (error !== 'cancel') {
          // 处理不同的错误格式
          const errorMsg = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            '上传失败';
          ElMessage.error('上传小程序失败：' + errorMsg);
          console.error('上传小程序错误:', error);
        }
      } finally {
        this.loading.upload = false;
      }
    },

    // 生成小程序二维码
    async generateMiniprogramQrcode() {
      this.loading.qrcode = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'generate_miniprogram_qrcode',
            store_id: this.userInfo.id
          }
        );

        if (response.data.success) {
          ElMessage.success('小程序二维码生成成功');
        } else {
          ElMessage.error(response.data.error || '生成失败');
        }
      } catch (error) {
        ElMessage.error('生成小程序二维码失败');
        console.error('生成小程序二维码错误:', error);
      } finally {
        this.loading.qrcode = false;
      }
    },

    // 生成公众号二维码
    async generateOffiaccountQrcode() {
      this.loading.qrcode = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/platform_management.php`,
          {
            action: 'generate_offiaccount_qrcode',
            store_id: this.userInfo.id
          }
        );

        if (response.data.success) {
          this.offiaccountQrcode = response.data.qrcode_url;
          ElMessage.success('公众号二维码生成成功');
        } else {
          ElMessage.error(response.data.error || '生成失败');
        }
      } catch (error) {
        ElMessage.error('生成公众号二维码失败');
        console.error('生成公众号二维码错误:', error);
      } finally {
        this.loading.qrcode = false;
      }
    },

    // 复制到剪贴板
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        ElMessage.success('已复制到剪贴板');
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        ElMessage.success('已复制到剪贴板');
      }
    }
  }
};
</script>

<style scoped lang="scss">
.page-container {
  padding: 20px;
  min-height: 100vh;
}

.settings-form {
  max-width: 100%;
  margin: 0 auto;
}

/* 引导区域 */
.guide-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 24px;
  background: linear-gradient(135deg, #409eff 0%, #3375b9 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.guide-content {
  .guide-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 8px;

    .guide-icon {
      font-size: 28px;
    }
  }

  .guide-desc {
    font-size: 14px;
    opacity: 0.9;
    margin: 0;
  }
}

.guide-actions {
  .el-button {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

/* 卡片样式 */
.platform-card {
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid #ebeef5;
  background: #ffffff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  background: #f8fafc;
  border-radius: 12px 12px 0 0;
}

.card-title-section {
  display: flex;
  align-items: center;
  gap: 12px;

  .card-icon {
    font-size: 20px;

    &.mini-icon {
      color: #67c23a;
    }

    &.offi-icon {
      color: #e6a23c;
    }

    &.h5-icon {
      color: #409eff;
    }
  }

  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }
}

.save-btn {
  background: linear-gradient(135deg, #409eff 0%, #3375b9 100%);
  border: none;
  color: white;

  &:hover {
    background: linear-gradient(135deg, #3375b9 0%, #409eff 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
  }
}

/* 表单内容 */
.form-content {
  padding: 20px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #606266;
}

/* 私钥输入框样式 */
.private-key-input {
  :deep(.el-textarea__inner) {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    line-height: 1.4;
  }
}

.key-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;
  margin-top: 6px;

  .el-icon {
    color: #e6a23c;
  }
}

/* 开关样式 */
.switch-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.switch-label {
  color: #909399;
  font-size: 14px;

  &.active {
    color: #67c23a;
    font-weight: 500;
  }
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  border: none;

  &.el-button--success {
    background: linear-gradient(135deg, #67c23a 0%, #529b2e 100%);
    color: white;

    &:hover {
      background: linear-gradient(135deg, #529b2e 0%, #67c23a 100%);
    }
  }

  &.el-button--warning {
    background: linear-gradient(135deg, #e6a23c 0%, #d4821a 100%);
    color: white;

    &:hover {
      background: linear-gradient(135deg, #d4821a 0%, #e6a23c 100%);
    }
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

/* URL输入框样式 */
.url-input {
  :deep(.el-input-group__append) {
    background: #409eff;
    border: none;
    color: white;

    .copy-btn {
      color: white;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }
}

.url-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}

/* 更新信息 */
.update-info {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-icon {
    color: #909399;
  }

  .update-time {
    color: #909399;
    font-size: 14px;
  }
}

/* 二维码容器 */
.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.qrcode-image {
  width: 200px;
  height: 200px;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.qrcode-tip {
  margin-top: 10px;
  color: #606266;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .guide-section {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .action-buttons {
    flex-direction: column;
  }

  :deep(.el-form-item) {
    margin-bottom: 16px;
  }
}
</style>